/**
 * Configuration for error button behavior
 */
export interface ErrorButtonConfig {
  text: string;
  onClick: () => void;
}

/**
 * Get button configuration based on HTTP status code
 *
 * @param status - HTTP status code (e.g., 404, 500) or undefined for network errors
 * @param navigate - Navigation function from react-router-dom (required for 4xx errors)
 * @returns Button configuration or null if no button should be shown
 *
 * @example
 * ```typescript
 * const navigate = useNavigate();
 * const buttonConfig = getErrorButtonConfig(404, navigate);
 * // Returns: { text: 'Go Home', onClick: () => navigate('/') }
 * ```
 *
 * @example
 * ```typescript
 * const buttonConfig = getErrorButtonConfig(500);
 * // Returns: { text: 'Try Again', onClick: () => window.location.reload() }
 * ```
 *
 * @example
 * ```typescript
 * const buttonConfig = getErrorButtonConfig(undefined);
 * // Returns: { text: 'Try Again', onClick: () => window.location.reload() }
 * ```
 */
export function getErrorButtonConfig(
  status?: number,
  navigate?: (path: string) => void,
): ErrorButtonConfig | null {
  // 4xx errors - client errors (Go Home)
  if (status && status >= 400 && status < 500) {
    if (!navigate) {
      console.warn('getErrorButtonConfig: navigate function is required for 4xx errors');
      return null;
    }
    return {
      text: 'Go Home',
      onClick: () => navigate('/'),
    };
  }

  // Network errors (no status) or 5xx errors - show "Try Again" button
  if (!status || (status >= 500 && status < 600)) {
    return {
      text: 'Try Again',
      onClick: () => window.location.reload(),
    };
  }

  return null;
}
